import * as Sentry from '@sentry/react';
import React from 'react';
import ReactDOM from 'react-dom';
import { BrowserRouter } from 'react-router-dom';
import App from './App';
import { getBrandFromHost } from './modules/brandInfo';
import { BrandType } from './typewriter/enums';

const baseUrl = document.getElementsByTagName('base')[0].getAttribute('href');
const rootElement = document.getElementById('root');

let environment: string | undefined = undefined;

if (isProdBuild) {
    environment = 'production';
} else if (isDevBuild) {
    environment = 'development';
}

if (/-staging|-qa\d+\./.test(window.location.host)) {
    environment = 'staging';

    // if this is QA, lets set that environment more specifically
    const qaRegex = /booking-(qa\d+)\./;
    const match = window.location.host.match(qaRegex);

    if (match) {
        environment = match[1];
    }
}

const brandInfo = getBrandFromHost(window.location.host);

// The GTM SST snippet is injected server-side into the HTML <head> by the BookingSubdomain host
// (BookingGtmInjection.cs), before the bundle loads - no client-side injection here.

// load FullStory for MovingStaffers only on production (not staging/QA)
// isProdBuild is true for all deployed builds including staging; use the runtime
// environment variable (which overrides to 'staging'/'qa*' for non-prod hosts)
if (
    environment === 'production' &&
    (brandInfo.brand === BrandType.MovingStaffers || brandInfo.brand === BrandType.MsMarketplace)
) {
    const fsScript = document.createElement('script');
    fsScript.innerHTML = `
window['_fs_host'] = 'fullstory.com';
window['_fs_script'] = 'edge.fullstory.com/s/fs.js';
window['_fs_org'] = 'o-22QP61-na1';
window['_fs_namespace'] = 'FS';
!function(m,n,e,t,l,o,g,y){var s,f,a=function(h){
return!(h in m)||(m.console&&m.console.log&&m.console.log('FullStory namespace conflict. Please set window["_fs_namespace"].'),!1)}(e)
;function p(b){var h,d=[];function j(){h&&(d.forEach((function(b){var d;try{d=b[h[0]]&&b[h[0]](h[1])}catch(h){return void(b[3]&&b[3](h))}
d&&d.then?d.then(b[2],b[3]):b[2]&&b[2](d)})),d.length=0)}function r(b){return function(d){h||(h=[b,d],j())}}return b(r(0),r(1)),{
then:function(b,h){return p((function(r,i){d.push([b,h,r,i]),j()}))}}}a&&(g=m[e]=function(){var b=function(b,d,j,r){function i(i,c){
h(b,d,j,i,c,r)}r=r||2;var c,u=/Async$/;return u.test(b)?(b=b.replace(u,""),"function"==typeof Promise?new Promise(i):p(i)):h(b,d,j,c,c,r)}
;function h(h,d,j,r,i,c){return b._api?b._api(h,d,j,r,i,c):(b.q&&b.q.push([h,d,j,r,i,c]),null)}return b.q=[],b}(),y=function(b){function h(h){
"function"==typeof h[4]&&h[4](new Error(b))}var d=g.q;if(d){for(var j=0;j<d.length;j++)h(d[j]);d.length=0,d.push=h}},function(){
(o=n.createElement(t)).async=!0,o.crossOrigin="anonymous",o.src="https://"+l,o.onerror=function(){y("Error loading "+l)}
;var b=n.getElementsByTagName(t)[0];b&&b.parentNode?b.parentNode.insertBefore(o,b):n.head.appendChild(o)}(),function(){function b(){}
function h(b,h,d){g(b,h,d,1)}function d(b,d,j){h("setProperties",{type:b,properties:d},j)}function j(b,h){d("user",b,h)}function r(b,h,d){j({
uid:b},d),h&&j(h,d)}g.identify=r,g.setUserVars=j,g.identifyAccount=b,g.clearUserCookie=b,g.setVars=d,g.event=function(b,d,j){h("trackEvent",{
name:b,properties:d},j)},g.anonymize=function(){r(!1)},g.shutdown=function(){h("shutdown")},g.restart=function(){h("restart")},
g.log=function(b,d){h("log",{level:b,msg:d})},g.consent=function(b){h("setIdentity",{consent:!arguments.length||b})}}(),s="fetch",
f="XMLHttpRequest",g._w={},g._w[f]=m[f],g._w[s]=m[s],m[s]&&(m[s]=function(){return g._w[s].apply(this,arguments)}),g._v="2.0.0")
}(window,document,window._fs_namespace,"script",window._fs_script);`;
    document.head.appendChild(fsScript);
}

window.dataLayer = window.dataLayer || [];

if (!window.gtag) {
    window.gtag = function () {
        // eslint-disable-next-line prefer-rest-params
        window.dataLayer.push(arguments);
    };
}

declare const hahVersion: string;

console.debug('🐛 init sentry inside of booking-subdomain spa', { devBuild: isDevBuild });

if (!isDevBuild) {
    Sentry?.init({
        dsn: 'https://51ac5277caed485da455f312b862edee@o98567.ingest.sentry.io/6326087', // mcheckout-rjs
        integrations: [
            Sentry.browserTracingIntegration({ enableInp: true } as any),
        ],

        environment: environment,
        denyUrls: [
            // Facebook flakiness
            /graph\.facebook\.com/i,
            // Facebook blocked
            /connect\.facebook\.net\/en_US\/all\.js/i,
            // Woopra flakiness
            /eatdifferent\.com\.woopra-ns\.com/i,
            /static\.woopra\.com\/js\/woopra\.js/i,
            // Chrome extensions
            /extensions\//i,
            /^chrome:\/\//i,
            // Other plugins
            /127\.0\.0\.1:4001\/isrunning/i,  // Cacaoweb
            /webappstoolbarba\.texthelp\.com\//i,
            /metrics\.itunes\.apple\.com\.edgesuite\.net\//i,
            /sumo\.com/i,
            /wistia\.com/i,
            /gstatic\.com\/call-tracking\//i,
            /bing\.com/i,
            /doubleclick\.net/i,
            /dca0\.com/i, // no idea what this garbage is
            /clickcease\.com/i,
            /googletagmanager\.com/i,
        ],
        beforeSend: (event) => {
            if (event && typeof event.message === 'string' && event.message.match(/401 Unauthorized/i)) {
                event.level = 'warning'; // dont log this as an error
            }
            if (event && typeof event.message === 'string' && event.message.match(/No connection/i)) {
                event.level = 'warning'; // dont log this as an error
            }

            return event;
        },
        release: `booking-subdomain-index@${hahVersion}`,

        tracesSampleRate: 0.01 // 1%
    });
}

ReactDOM.render(
    <BrowserRouter basename={baseUrl!}>
        <App />
    </BrowserRouter>,
    rootElement
);
